import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  CreateTaskGroupInput,
  PaginatedTaskGroupsResponse,
  TaskGroup,
  TaskGroupParticipantChanges,
  UpdateTaskGroupInput,
} from '@this+that/models';

const GET_PAGINATED_TASK_GROUPS = gql`
  query getPaginatedTaskGroups(
    $limit: Int!
    $exclusiveStartKey: String
    $teamId: String
    $includeDefaultTaskGroup: Boolean
  ) {
    getPaginatedTaskGroups(
      limit: $limit
      exclusiveStartKey: $exclusiveStartKey
      teamId: $teamId
      includeDefaultTaskGroup: $includeDefaultTaskGroup
    ) {
      taskGroups {
        userId
        id
        teamId
        hexColor
        createdFor
        createdAt
        updatedAt
        title
        participantIds
        participants {
          id
          name
          email
          title
        }
      }
      exclusiveStartKey
    }
  }
`;

interface GetPaginatedTaskGroupsParams {
  limit: number;
  exclusiveStartKey?: string;
  teamId?: string;
  includeDefaultTaskGroup?: boolean;
}

interface GetPaginatedTaskGroupsData {
  getPaginatedTaskGroups: PaginatedTaskGroupsResponse;
}

interface CreateTaskGroupData {
  createTaskGroup: TaskGroup;
}

interface UpdateTaskGroupData {
  updateTaskGroup: TaskGroup;
}

interface DeleteTaskGroupData {
  deleteTaskGroup: boolean;
}

export function useGetPaginatedTaskGroups() {
  const { loading, refetch } = useQuery<GetPaginatedTaskGroupsData>(
    GET_PAGINATED_TASK_GROUPS,
    {
      skip: true,
    },
  );

  const getPaginatedTaskGroups = useCallback(
    async ({
      limit,
      exclusiveStartKey,
      teamId,
      includeDefaultTaskGroup,
    }: GetPaginatedTaskGroupsParams): Promise<PaginatedTaskGroupsResponse> => {
      const { data } = await refetch({
        limit,
        exclusiveStartKey,
        teamId,
        includeDefaultTaskGroup,
      });

      return (
        data?.getPaginatedTaskGroups ?? {
          taskGroups: [],
          exclusiveStartKey: null,
        }
      );
    },
    [refetch],
  );

  return { getPaginatedTaskGroups, loading };
}

const GET_TASK_GROUP = gql`
  query getTaskGroup($taskGroupId: String!) {
    getTaskGroup(taskGroupId: $taskGroupId) {
      id
      teamId
    }
  }
`;

type TaskGroupTeamLookup = Pick<TaskGroup, 'id' | 'teamId'>;

interface GetTaskGroupData {
  getTaskGroup: TaskGroupTeamLookup | null;
}

export function useGetTaskGroup() {
  const { loading, refetch } = useQuery<GetTaskGroupData>(GET_TASK_GROUP, {
    skip: true,
  });

  const getTaskGroup = useCallback(
    async (taskGroupId: string): Promise<TaskGroupTeamLookup | null> => {
      const { data } = await refetch({ taskGroupId });
      return data?.getTaskGroup ?? null;
    },
    [refetch],
  );

  return { getTaskGroup, loading };
}

const CREATE_TASK_GROUP = gql`
  mutation createTaskGroup($taskGroup: CreateTaskGroupInput!) {
    createTaskGroup(taskGroup: $taskGroup) {
      userId
      id
      teamId
      hexColor
      createdFor
      createdAt
      updatedAt
      title
      participantIds
      participants {
        id
        name
        email
        title
      }
    }
  }
`;

export function useCreateTaskGroup() {
  const [createTaskGroupMutation, { loading, error }] =
    useMutation<CreateTaskGroupData>(CREATE_TASK_GROUP);

  const createTaskGroup = useCallback(
    async (taskGroup: CreateTaskGroupInput) => {
      const { data } = await createTaskGroupMutation({
        variables: { taskGroup },
      });

      return data?.createTaskGroup;
    },
    [createTaskGroupMutation],
  );

  return { createTaskGroup, loading, error };
}

const UPDATE_TASK_GROUP = gql`
  mutation updateTaskGroup($taskGroup: UpdateTaskGroupInput!) {
    updateTaskGroup(taskGroup: $taskGroup) {
      userId
      id
      teamId
      hexColor
      createdFor
      createdAt
      updatedAt
      title
      participantIds
      participants {
        id
        name
        email
        title
      }
    }
  }
`;

export function useUpdateTaskGroup() {
  const [updateTaskGroupMutation, { loading, error }] =
    useMutation<UpdateTaskGroupData>(UPDATE_TASK_GROUP);

  const updateTaskGroup = useCallback(
    async (taskGroup: UpdateTaskGroupInput) => {
      const { data } = await updateTaskGroupMutation({
        variables: { taskGroup },
      });

      return data?.updateTaskGroup;
    },
    [updateTaskGroupMutation],
  );

  return { updateTaskGroup, loading, error };
}

const DELETE_TASK_GROUP = gql`
  mutation deleteTaskGroup($taskGroupId: String!) {
    deleteTaskGroup(taskGroupId: $taskGroupId)
  }
`;

export function useDeleteTaskGroup() {
  const [deleteTaskGroupMutation, { loading, error }] =
    useMutation<DeleteTaskGroupData>(DELETE_TASK_GROUP);

  const deleteTaskGroup = useCallback(
    async (taskGroupId: string) => {
      const { data } = await deleteTaskGroupMutation({
        variables: { taskGroupId },
      });

      return data?.deleteTaskGroup ?? false;
    },
    [deleteTaskGroupMutation],
  );

  return { deleteTaskGroup, loading, error };
}

const UPDATE_TASK_GROUP_PARTICIPANTS = gql`
  mutation updateTaskGroupParticipants(
    $taskGroupId: String!
    $participantChanges: ParticipantChangesInput!
  ) {
    updateTaskGroupParticipants(
      taskGroupId: $taskGroupId
      participantChanges: $participantChanges
    ) {
      userId
      id
      teamId
      hexColor
      createdFor
      createdAt
      updatedAt
      title
      participantIds
      participants {
        id
        name
        email
        title
      }
    }
  }
`;

interface UpdateTaskGroupParticipantsData {
  updateTaskGroupParticipants: TaskGroup | null;
}

export function useUpdateTaskGroupParticipants() {
  const [updateTaskGroupParticipantsMutation, { loading, error }] =
    useMutation<UpdateTaskGroupParticipantsData>(
      UPDATE_TASK_GROUP_PARTICIPANTS,
    );

  const updateTaskGroupParticipants = useCallback(
    async (
      taskGroupId: string,
      participantChanges: TaskGroupParticipantChanges,
    ) => {
      const { data } = await updateTaskGroupParticipantsMutation({
        variables: { taskGroupId, participantChanges },
      });

      return data?.updateTaskGroupParticipants ?? null;
    },
    [updateTaskGroupParticipantsMutation],
  );

  return { updateTaskGroupParticipants, loading, error };
}
